import type {
  EmbedPresentationView,
  EmbedShare,
  EmbedShareInfo,
  EmbedViewType,
} from '@/types/embed.types';
import type { PdfQuality } from '@genesisframeworks/viewer-plugin';

type LocationLike = Pick<Location, 'origin' | 'search'>;
type PublicFileConnection = {
  publicUrl?: string;
  prefix?: string;
};

export const DEFAULT_GENESIS_VIEW_BASE_URL = 'https://view.genesisframeworks.com/';

interface PublicEmbedUrlOptions {
  publicUrl?: string;
  locationLike?: LocationLike;
  basePath?: string;
  /**
   * Ignore share.publicUrl and build on the Box/app origin. Used only when
   * showing an explicit fallback next to the customer-domain primary link.
   */
  forceBoxOrigin?: boolean;
  /** Presentation state is encoded in the URL, never in share identity. */
  view?: EmbedPresentationView;
  layout?: 'filmstrip' | 'sidebar' | 'slider' | 'grid';
  album?: string;
  image?: string;
  book?: string;
  page?: number;
  quality?: PdfQuality;
}

function normalizeBasePath(basePath: string): string {
  const value = basePath || '/';
  return value.endsWith('/') ? value : `${value}/`;
}

function normalizeViewerBaseUrl(baseUrl: string): string {
  const value = String(baseUrl || DEFAULT_GENESIS_VIEW_BASE_URL).trim() || DEFAULT_GENESIS_VIEW_BASE_URL;
  return value.endsWith('/') ? value : `${value}/`;
}

function encodePathSegments(value: string): string {
  return String(value || '')
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join('/');
}

function normalizePublicUrl(value?: string): string {
  return String(value || '').trim().replace(/\/+$/, '');
}

export function buildEmbedPageUrl(
  shareId: string,
  locationLike: LocationLike = window.location,
  basePath = import.meta.env.BASE_URL || '/',
): string {
  const url = new URL(`${normalizeBasePath(basePath)}embed/${encodeURIComponent(shareId)}`, locationLike.origin);
  const currentParams = new URLSearchParams(locationLike.search || '');
  const apiBase = currentParams.get('apiBase');
  if (apiBase) url.searchParams.set('apiBase', apiBase);
  return url.toString();
}

export function buildGenesisViewPdfUrl(
  pdfUrl: string,
  viewerBaseUrl = import.meta.env.VITE_GENESIS_VIEW_BASE_URL || DEFAULT_GENESIS_VIEW_BASE_URL,
): string {
  const url = new URL('smart.html', normalizeViewerBaseUrl(viewerBaseUrl));
  url.searchParams.set('url', pdfUrl);
  return url.toString();
}

export function buildConnectionPublicFileUrl(
  filePath: string,
  connection?: PublicFileConnection | null,
): string | null {
  const publicUrl = normalizePublicUrl(connection?.publicUrl);
  if (!publicUrl) return null;

  const path = [connection?.prefix || '', filePath]
    .map((part) => String(part || '').replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
  const encodedPath = encodePathSegments(path);

  return new URL(encodedPath, `${publicUrl}/`).toString();
}

export function buildConnectionPublicFolderUrl(
  folderPath: string,
  connection?: PublicFileConnection | null,
): string | null {
  const url = buildConnectionPublicFileUrl(folderPath, connection);
  if (!url) return null;
  return url.endsWith('/') ? url : `${url}/`;
}

export function buildConnectionPdfViewerUrl(
  filePath: string,
  connection?: PublicFileConnection | null,
  viewerBaseUrl?: string,
): string | null {
  const publicFileUrl = buildConnectionPublicFileUrl(filePath, connection);
  return publicFileUrl ? buildGenesisViewPdfUrl(publicFileUrl, viewerBaseUrl) : null;
}

/**
 * Build the public share URL for an EmbedShare.
 *
 * Returns the canonical /embed/:shareId route which mounts
 * StandaloneEmbedShellPage — the stable public share entry route.
 * Gallery shares render inline in Box there; PDF shares resolve from that
 * entry route into the canonical GenesisView viewer flow. This is distinct
 * from the owner-mode `/drive/?share=` route.
 *
 * If `options.publicUrl` or `share.publicUrl` is set (the customer's R2
 * custom domain), the URL is built against that origin instead of the Box origin —
 * customers see e.g. `https://drive.opendoorpc.org/embed/A7xK9p2m`
 * which a Cloudflare Worker rule rewrites to box.genesisframeworks.com
 * (or proxies the SPA, depending on the customer's CF setup).
 *
 * The `?view=` query param is no longer needed. `share.viewType` remains the
 * authoritative behavior switch for `/embed/:shareId`: gallery stays inline,
 * while `pdf-reader` and `pdf-library` redirect to GenesisView.
 */
export function buildPublicDriveEmbedUrl(
  share: EmbedShare,
  options: PublicEmbedUrlOptions = {},
): string {
  const locationLike = options.locationLike || window.location;
  const basePath = options.basePath ?? (import.meta.env.BASE_URL || '/');
  const publicUrl = options.forceBoxOrigin
    ? ''
    : normalizePublicUrl(options.publicUrl || share.publicUrl);
  const origin = publicUrl || locationLike.origin;
  // When publicUrl is the customer's R2 domain, basePath is always '/'
  // (the customer doesn't host the SPA under a sub-path).
  const path = publicUrl
    ? '/'
    : normalizeBasePath(basePath);
  const routeToken = share.slug || share.id;
  const url = new URL(`${path}embed/${encodeURIComponent(routeToken)}`, origin);
  // Preserve apiBase override for dev (irrelevant on customer domain
  // because the customer's CF Worker handles routing, but harmless).
  if (!publicUrl) {
    const currentParams = new URLSearchParams(locationLike.search || '');
    const apiBase = currentParams.get('apiBase');
    if (apiBase) url.searchParams.set('apiBase', apiBase);
  }
  if (options.view) url.searchParams.set('view', options.view);
  if (options.layout) url.searchParams.set('layout', options.layout);
  if (options.album) url.searchParams.set('album', options.album);
  if (options.image) url.searchParams.set('image', options.image);
  if (options.book) url.searchParams.set('book', options.book);
  if (options.page && options.page > 0) url.searchParams.set('page', String(options.page));
  if (options.quality && options.quality !== 'auto') url.searchParams.set('quality', options.quality);
  return url.toString();
}

export function buildEmbedShareInfo(
  share: EmbedShare,
  options: PublicEmbedUrlOptions = {},
): EmbedShareInfo {
  const url = buildPublicDriveEmbedUrl(share, options);
  return {
    url,
    snippet: buildEmbedIframeSnippet(share, url, options.view),
  };
}

export function embedViewTypeForViewerMode(
  mode: string,
  contentType: 'image' | 'pdf',
): EmbedViewType {
  if (contentType === 'image') return 'gallery';
  return mode === 'reader' || mode === 'pdf-reader' ? 'pdf-reader' : 'pdf-library';
}

export function presentationViewForViewerMode(
  mode: string,
  contentType: 'image' | 'pdf',
): EmbedPresentationView {
  if (contentType === 'image') {
    if (mode === 'image-carousel' || mode === 'carousel') return 'carousel';
    if (mode === 'image-viewer' || mode === 'multiview') return 'multiview';
    return 'gallery';
  }
  if (mode === 'pdf-list' || mode === 'list') return 'pdf-list';
  if (mode === 'pdf-reader' || mode === 'reader') return 'pdf-reader';
  if (mode === 'pdf-flipbook' || mode === 'flipbook') return 'pdf-flipbook';
  return 'pdf-library';
}

export function buildEmbedIframeSnippet(
  share: EmbedShare,
  src: string,
  presentationView?: EmbedPresentationView,
): string {
  const effectiveView = presentationView || share.viewType;
  const height = effectiveView === 'gallery' || effectiveView === 'carousel' || effectiveView === 'multiview'
    ? '820px'
    : '900px';
  const title = escapeAttribute(share.title || 'GenesisBox embed');
  return [
    '<iframe',
    `  src="${escapeAttribute(src)}"`,
    `  title="${title}"`,
    '  style="width:100%; height:' + height + '; border:0; display:block;"',
    '  loading="lazy"',
    '  allowfullscreen',
    '></iframe>',
  ].join('\n');
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
